import { Routes, Route } from '@angular/router';
import { authGuard } from '@core/guards/auth.guard';
import { mfaRequiredGuard } from '@core/guards/mfa-required.guard';
import { roleGuard, permissionGuard } from '@core/guards/role.guard';
import { PERMISSION_GROUPS, ROLES } from '@core/constants/permissions';
import { tenantGuard } from '@core/guards/tenant.guard';
import { devToolsGuard } from '@core/guards/dev-tools.guard';
import { featureFlagGuard } from '@core/guards/feature-flag.guard';
import { environment } from '../environments/environment';
import { USER_MANAGEMENT_ROLES } from './features/users/models/user-permissions';
import { USER_ADMIN_ROLES } from './features/users/models/user-capabilities';
import { devRoutes } from './dev-routes';
import { AUTO_OPTIMIZATION_SURFACE_ENABLED } from '@core/config/deployment-surfaces';

const allRoutes: Routes = [
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },
  {
    path: 'auth',
    loadChildren: () => import('./features/auth/auth.routes').then(m => m.authRoutes),
    data: { preload: false } // Auth routes don't need preloading
  },
  {
    path: 'tenant-selection',
    canActivate: [authGuard, mfaRequiredGuard],
    loadChildren: () => import('./features/tenant-selection/tenant-selection.routes').then(m => m.TENANT_SELECTION_ROUTES),
    data: { preload: false } // Tenant selection doesn't need preloading
  },
  {
    path: 'dashboard',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard],
    loadChildren: () => import('./features/dashboard/dashboard.routes').then(m => m.dashboardRoutes),
    data: { preload: true, priority: 'high' } // High priority for immediate preloading
  },
  {
    path: 'symbols',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.SYMBOL_ADMIN, ROLES.SCHEMA_EDITOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN], preload: true, priority: 'high' },
    loadChildren: () => import('./features/symbols/symbol.routes').then(m => m.symbolRoutes)
  },
  {
    path: 'tenants',
    canActivate: [authGuard, mfaRequiredGuard, roleGuard],
    data: { requiredRoles: [ROLES.SYSTEM_ADMIN], preload: true, priority: 'medium' },
    loadChildren: () => import('./features/tenants/tenant.routes').then(m => m.tenantRoutes)
  },
  {
    path: 'users',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, permissionGuard],
    // GAP-FE-AR10: narrowed to admin-tier roles to preserve historical access surface
    // (USER_MANAGER was a dead string — never issued by the backend).
    // Roles come from USER_ADMIN_ROLES — the same set the capability predicate
    // uses, mirroring the backend router's AuthContext::is_admin(). Previously
    // this admitted tenant-admin (which the backend rejects outright, so the
    // page 403'd on its own list call) while excluding super_admin (the ONLY
    // role the mutation handlers accept), so a super_admin-only session was
    // redirected away from the page it alone can fully use.
    data: { requiredRoles: [...USER_ADMIN_ROLES], requiredPermissions: ['users:read'], preload: true, priority: 'medium' },
    loadChildren: () => import('./features/users/user.routes').then(m => m.userRoutes)
  },
  {
    path: 'permissions-management',
    // roleGuard before tenantGuard: reject unauthorized users before tenant-selection ceremony
    canActivate: [authGuard, mfaRequiredGuard, roleGuard, tenantGuard],
    data: { requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN], allowSystemAdminBypass: true, preload: true, priority: 'high' },
    loadChildren: () => import('./features/permissions-management/permissions-management.routes').then(m => m.PERMISSIONS_ROUTES)
  },
  {
    path: 'settings',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard],
    data: { preload: true, priority: 'medium' },
    loadChildren: () => import('./features/settings/settings.routes').then(m => m.settingsRoutes)
  },
  {
    path: 'reports',
    // No roleGuard: reports are intentionally accessible to all authenticated users with tenant context
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard],
    data: { preload: true, priority: 'low' },
    loadChildren: () => import('./features/reports/reports.routes').then(m => m.reportsRoutes)
  },
  {
    path: 'admin/symbol-mapping',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.SYMBOL_ADMIN, ROLES.SCHEMA_EDITOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN], preload: true, priority: 'high' },
    loadChildren: () => import('./features/symbol-mapping/symbol-mapping.routes').then(m => m.symbolMappingRoutes)
  },
  {
    path: 'admin/control-panel',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.SYSTEM_ADMIN],
      preload: false,
      priority: 'medium'
    },
    loadChildren: () => import('./features/admin-control-panel/admin-control-panel.routes').then(m => m.ADMIN_CONTROL_PANEL_ROUTES)
  },
  {
    path: 'admin/audit',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.TENANT_VIEWER, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN],
      preload: false,
      priority: 'medium'
    },
    loadChildren: () => import('./features/audit-logs/audit-logs.routes').then(m => m.auditLogsRoutes)
  },
  {
    // GAP-FE-A50: feature-flag gate runs BEFORE roleGuard so disabled
    // routes 404 (the route behaves as if it does not exist). GAP-050:
    // the copilot backend is live; the flag is a pure FE rollout switch.
    path: 'copilot-summaries',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, featureFlagGuard('enableCopilotSummaries'), roleGuard],
    data: {
      requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN],
      preload: false,
      priority: 'low'
    },
    loadChildren: () =>
      import('./features/copilot-summaries/copilot-summaries.routes').then(m => m.copilotSummariesRoutes)
  },
  {
    // GAP-FE-A50: feature-flag gate; route 404s when enableAgentProposals=false.
    path: 'agent-proposals',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, featureFlagGuard('enableAgentProposals'), roleGuard],
    data: {
      // Same role gating as copilot-summaries; the per-action approve/
      // reject endpoints additionally require ops_approver role on
      // the backend (enforced by route handlers, not the route guard).
      requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN],
      preload: false,
      priority: 'low'
    },
    loadChildren: () =>
      import('./features/agent-proposals/agent-proposals.routes').then(m => m.agentProposalsRoutes)
  },
  {
    // GAP-FE-A50: feature-flag gate; route 404s when enableAgentInvariants=false.
    path: 'agent-invariants',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, featureFlagGuard('enableAgentInvariants'), roleGuard],
    data: {
      // GAP-B1: ops_admin / super_admin can mutate; ops_approver can read.
      // Backend enforces ops_admin / super_admin on PUT — the guard here
      // only gates the route's existence at the navigation layer.
      requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN, ROLES.TENANT_OPERATOR],
      preload: false,
      priority: 'low'
    },
    loadChildren: () =>
      import('./features/agent-invariants/agent-invariants.routes').then(m => m.agentInvariantsRoutes)
  },
  {
    // GAP-FE-A50: feature-flag gate; route 404s when enableAgentControls=false.
    path: 'agent-controls',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, featureFlagGuard('enableAgentControls'), roleGuard],
    data: {
      // GAP-B2: tenant-wide kill switch + per-symbol mode switcher.
      // BE enforces ops_admin / super_admin on POST; route guard
      // gates navigation only.
      requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN],
      preload: false,
      priority: 'low'
    },
    loadChildren: () =>
      import('./features/agent-controls/agent-controls.routes').then(m => m.agentControlsRoutes)
  },
  {
    path: 'data-quality',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN], preload: true, priority: 'medium' },
    loadChildren: () => import('./features/data-quality/data-quality.routes').then(m => m.dataQualityRoutes)
  },
  {
    path: 'system-health',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR], preload: true, priority: 'high' },
    loadChildren: () => import('./features/system-health/system-health.routes').then(m => m.systemHealthRoutes)
  },
  {
    path: 'strategy-builder',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN], preload: true, priority: 'medium' },
    loadChildren: () => import('./features/strategy-builder/strategy-builder.routes').then(m => m.strategyBuilderRoutes)
  },
  {
    path: 'backtests',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN],
      preload: true,
      priority: 'high'
    },
    loadChildren: () => import('./features/backtesting/backtesting.routes').then(m => m.backtestingRoutes)
  },
  {
    path: 'features/feature-sets',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN],
      preload: true,
      priority: 'medium'
    },
    loadChildren: () => import('./features/feature-sets/feature-sets.routes').then(m => m.featureSetsRoutes)
  },
  {
    path: 'search',
    // No roleGuard: global search is intentionally accessible to all authenticated users with tenant context
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard],
    data: { preload: false },
    loadChildren: () => import('./features/search/search.routes').then(m => m.searchRoutes)
  },
  {
    path: 'trading',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN],
      preload: true,
      priority: 'high'
    },
    loadChildren: () => import('./features/trading/trading.routes').then(m => m.tradingRoutes)
  },
  {
    path: 'trading-sessions',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN],
      preload: false,
      priority: 'medium'
    },
    loadChildren: () => import('./features/trading-sessions/trading-sessions.routes').then(m => m.tradingSessionsRoutes)
  },
  {
    path: 'market-making',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN],
      preload: false,
      priority: 'medium'
    },
    loadChildren: () => import('./features/market-making/market-making.routes').then(m => m.MARKET_MAKING_ROUTES)
  },
  {
    path: 'strategies',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN],
      preload: true,
      priority: 'medium'
    },
    loadChildren: () => import('./features/strategies/strategies.routes').then(m => m.strategiesRoutes)
  },
  {
    path: 'config-management',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN],
      preload: true,
      priority: 'medium'
    },
    loadChildren: () => import('./features/config-management/config-management.routes').then(m => m.CONFIG_MANAGEMENT_ROUTES)
  },
  {
    path: 'schema-management',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.SCHEMA_EDITOR, ROLES.DBA, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN],
      preload: true,
      priority: 'medium'
    },
    loadChildren: () => import('./features/schema-management/schema-management.routes').then(m => m.schemaManagementRoutes)
  },
  {
    path: 'exchanges',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN],
      preload: true,
      priority: 'medium'
    },
    loadChildren: () => import('./features/exchange-management/exchange-management.routes').then(m => m.exchangeManagementRoutes)
  },
  {
    path: 'help',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard],
    data: { preload: false, allowSystemAdminBypass: true },
    loadChildren: () => import('./features/help/help.routes').then(m => m.helpRoutes)
  },
  {
    path: 'config',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: {
      requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN],
      preload: true,
      priority: 'medium'
    },
    loadChildren: () => import('./features/environment-config/environment-config.routes').then(m => m.environmentConfigRoutes)
  },
  {
    path: 'risk-management',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: true, priority: 'high' },
    loadChildren: () => import('./features/risk-management/risk-management.routes').then(m => m.RISK_MANAGEMENT_ROUTES)
  },
  {
    path: 'risk-controls',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: true, priority: 'critical' },
    loadChildren: () => import('./features/risk-controls/risk-controls.routes').then(m => m.RISK_CONTROLS_ROUTES)
  },
  {
    path: 'risk-profiles',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: false, priority: 'medium' },
    loadChildren: () => import('./features/risk-profiles/risk-profiles.routes').then(m => m.RISK_PROFILES_ROUTES)
  },
  {
    path: 'telemetry',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.SYSTEM_ADMIN], preload: false, priority: 'low' },
    loadChildren: () => import('./features/telemetry/telemetry.routes').then(m => m.TELEMETRY_ROUTES)
  },
  ...(AUTO_OPTIMIZATION_SURFACE_ENABLED
    ? [{
        path: 'auto-optimization',
        canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
        data: { requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: false, priority: 'medium' },
        loadChildren: () => import('./features/auto-optimization/auto-optimization.routes').then(m => m.AUTO_OPTIMIZATION_ROUTES)
      }]
    : []),
  {
    path: 'auto-de-risking',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: false, priority: 'medium' },
    loadChildren: () => import('./features/auto-de-risking/auto-de-risking.routes').then(m => m.AUTO_DE_RISKING_ROUTES)
  },
  {
    path: 'order-book',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TRADER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: true, priority: 'medium' },
    loadChildren: () => import('./features/order-book/order-book.routes').then(m => m.ORDER_BOOK_ROUTES)
  },
  {
    path: 'pnl-analytics',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: true, priority: 'medium' },
    loadChildren: () => import('./features/pnl-analytics/pnl-analytics.routes').then(m => m.PNL_ANALYTICS_ROUTES)
  },
  {
    path: 'pl-dashboard',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: false, priority: 'medium' },
    loadChildren: () => import('./features/pl-dashboard/pl-dashboard.routes').then(m => m.PL_DASHBOARD_ROUTES)
  },
  {
    path: 'profitability',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN], preload: false, priority: 'high' },
    loadChildren: () => import('./features/profitability/profitability.routes').then(m => m.PROFITABILITY_ROUTES)
  },
  {
    path: 'ml-anomaly-detection',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, permissionGuard],
    data: { requiredPermissions: PERMISSION_GROUPS.ANOMALY_VIEW, requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN, ROLES.TENANT_OPERATOR], preload: true, priority: 'low' },
    loadChildren: () => import('./features/ml-anomaly-detection/ml-anomaly-detection.routes').then(m => m.ML_ANOMALY_DETECTION_ROUTES)
  },
  {
    path: 'ml-monitoring',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: false, priority: 'low' },
    loadChildren: () => import('./features/ml-monitoring/ml-monitoring.routes').then(m => m.ML_MONITORING_ROUTES)
  },
  {
    path: 'innovation-control',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, permissionGuard],
    data: {
      requiredPermissions: ['allocation:read'],
      preload: false,
      priority: 'low'
    },
    loadChildren: () => import('./features/innovation-control/innovation-control.routes').then(m => m.INNOVATION_CONTROL_ROUTES)
  },
  {
    path: 'paper-trading',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TRADER, ROLES.STRATEGY_MANAGER, ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: false, priority: 'medium' },
    loadChildren: () => import('./features/paper-trading/paper-trading.routes').then(m => m.PAPER_TRADING_ROUTES)
  },
  {
    path: 'predictive-maintenance',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: true, priority: 'low' },
    loadChildren: () => import('./features/predictive-maintenance/predictive-maintenance.routes').then(m => m.PREDICTIVE_MAINTENANCE_ROUTES)
  },
  {
    path: 'regulatory-reporting',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TENANT_ADMIN, ROLES.STRATEGY_MANAGER, ROLES.SYSTEM_ADMIN], preload: true, priority: 'medium' },
    loadChildren: () => import('./features/regulatory-reporting/regulatory-reporting.routes').then(m => m.REGULATORY_REPORTING_ROUTES)
  },
  {
    path: 'client-portal',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: true, priority: 'low' },
    loadChildren: () => import('./features/client-portal/client-portal.routes').then(m => m.CLIENT_PORTAL_ROUTES)
  },
  {
    path: 'client-accounts',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TENANT_OPERATOR, ROLES.SYSTEM_ADMIN], preload: false, priority: 'low' },
    loadChildren: () => import('./features/client-accounts/client-accounts.routes').then(m => m.CLIENT_ACCOUNTS_ROUTES)
  },
  {
    path: 'api-management',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN], preload: true, priority: 'medium' },
    loadChildren: () => import('./features/api-management/api-management.routes').then(m => m.API_MANAGEMENT_ROUTES)
  },
  {
    path: 'multi-tenant',
    // roleGuard before tenantGuard: reject unauthorized users immediately; sys-admins pass roleGuard then bypass tenantGuard via allowSystemAdminBypass
    canActivate: [authGuard, mfaRequiredGuard, roleGuard, tenantGuard],
    data: { requiredRoles: [ROLES.SYSTEM_ADMIN], allowSystemAdminBypass: true, preload: true, priority: 'medium' },
    loadChildren: () => import('./features/multi-tenant/multi-tenant.routes').then(m => m.MULTI_TENANT_ROUTES)
  },
  {
    path: 'heat-maps',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR, ROLES.TRADER, ROLES.STRATEGY_MANAGER], preload: true, priority: 'medium' },
    loadChildren: () => import('./features/heat-maps/heat-maps.routes').then(m => m.HEAT_MAPS_ROUTES)
  },
  {
    path: 'performance-monitoring',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR], preload: true, priority: 'medium' },
    loadChildren: () => import('./features/performance-monitoring/performance-monitoring.routes').then(m => m.PERFORMANCE_MONITORING_ROUTES)
  },
  {
    path: 'order-diagnostics',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR, ROLES.TRADER, ROLES.STRATEGY_MANAGER], preload: true, priority: 'high' },
    loadChildren: () => import('./features/order-diagnostics/order-diagnostics.routes').then(m => m.ORDER_DIAGNOSTICS_ROUTES)
  },
  {
    path: 'hft-metrics',
    canActivate: [authGuard, mfaRequiredGuard, tenantGuard, roleGuard],
    data: { requiredRoles: [ROLES.SYSTEM_ADMIN, ROLES.TENANT_ADMIN, ROLES.TENANT_OPERATOR], preload: true, priority: 'critical' },
    loadChildren: () => import('./features/hft-metrics/hft-metrics.routes').then(m => m.HFT_METRICS_ROUTES)
  },
  // GAP-FE-M002 · Dev-only routes are extracted from this array and
  // conditionally spliced in below based on the build-time
  // `environment.features.enableDevTools` constant. With prod's
  // literal `false`, the Angular optimizer dead-code-eliminates the
  // `developmentOnlyRoutes` reference and its lazy `import()`
  // chunks (`test-backend`, `style-guide`) never enter the bundle.
  {
    path: 'unauthorized',
    data: { preload: false }, // Error pages don't need preloading
    loadComponent: () => import('./shared/components/unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent)
  },
  // Redirect routes for backwards compatibility
  {
    path: 'backtesting',
    redirectTo: '/backtests',
    pathMatch: 'full'
  },
  {
    path: 'profile',
    redirectTo: '/settings',
    pathMatch: 'full'
  },
  {
    path: 'settings/profile',
    redirectTo: '/settings',
    pathMatch: 'full'
  },
  {
    path: 'risk/alerts',
    redirectTo: '/risk-management',
    pathMatch: 'full'
  },
  {
    path: 'admin/users',
    redirectTo: '/users',
    pathMatch: 'full'
  },
  {
    path: 'admin/system',
    redirectTo: '/system-health',
    pathMatch: 'full'
  },
  {
    path: 'permissions',
    redirectTo: '/permissions-management',
    pathMatch: 'full'
  },
  {
    path: 'schemas',
    redirectTo: '/schema-management',
    pathMatch: 'full'
  },
  {
    path: 'schemas/:a',
    redirectTo: '/schema-management/:a',
    pathMatch: 'full'
  },
  {
    path: 'schemas/:a/:b',
    redirectTo: '/schema-management/:a/:b',
    pathMatch: 'full'
  },
  {
    path: 'schemas/:a/:b/:c',
    redirectTo: '/schema-management/:a/:b/:c',
    pathMatch: 'full'
  },
  {
    path: '**',
    data: { preload: false }, // Error pages don't need preloading
    loadComponent: () => import('./shared/components/not-found/not-found.component').then(m => m.NotFoundComponent)
  }
];

/**
 * Filter out development-only routes in production builds
 *
 * Security: test-backend and style-guide routes expose development utilities
 * and diagnostic tools that should never be accessible in production.
 *
 * Defense-in-Depth Implementation (Three Layers):
 *
 * LAYER 1 (Build-time, dead-code elimination): `developmentOnlyRoutes`
 * is an isolated const containing the `loadComponent` lazy imports
 * for `test-backend` and `style-guide`. The conditional spread
 * `[...allRoutes, ...(environment.features.enableDevTools ? developmentOnlyRoutes : [])]`
 * is constant-foldable: when `enableDevTools` is `false` (prod /
 * staging), the optimizer drops the spread AND the import()
 * references it contains, so those chunks never enter the bundle.
 * The legacy runtime filter is retained for defensive parity.
 *
 * LAYER 2 (Runtime route filter): the legacy `isProductionRoute`
 * filter keeps the route table free of dev paths even if
 * developmentOnlyRoutes were ever spliced in by mistake.
 *
 * LAYER 3 (Runtime guard): devToolsGuard checks environment at
 * runtime and blocks access even if routes are mistakenly included
 * in production build.
 */
/**
 * GAP-FE-T06 r4 (codex round-2) · Exported (was previously file-local)
 * so the route spec can import this production source-of-truth rather
 * than duplicating the literal. A spec copy that drifts from this
 * value would silently let a new dev-only path leak into production
 * (or vice versa); the export ensures both the production filter
 * (`isProductionRoute` below) and the regression assertion in
 * app.routes.spec.ts iterate the same set.
 */
export const DEVELOPMENT_ONLY_ROUTE_PATHS: readonly string[] = ['test-backend', 'style-guide'] as const;
const developmentOnlyRoutePaths = DEVELOPMENT_ONLY_ROUTE_PATHS;

const isProductionRoute = (route: Route): boolean => {
  // Keep routes with undefined or empty paths (redirects, root)
  if (route.path === undefined || route.path === '') {
    return true;
  }
  // Keep wildcard route (404 handler)
  if (route.path === '**') {
    return true;
  }
  // Exclude development-only routes in production
  return !developmentOnlyRoutePaths.includes(route.path);
};

// GAP-FE-M002 · `devRoutes` resolves to the real dev-routes array
// from `./dev-routes` in dev builds, and to `[]` in production/
// staging via the `dev-routes.ts → dev-routes.prod.ts` angular.json
// fileReplacements swap. The empty array carries no `import()`
// references, so the test-backend (~128 KB) and style-guide (~91 KB)
// lazy chunks are dropped by tree-shake. The runtime
// `isProductionRoute` filter is retained for defense-in-depth in
// case a dev route is ever spliced into `allRoutes` by mistake.
//
// Route ordering: split the wildcard (`**`) catch-all out of
// `allRoutes` and re-append it AFTER `devRoutes`. Otherwise dev
// builds would route `/test-backend` and `/style-guide` to the
// 404 handler because Angular matches the first matching route
// (the wildcard) before reaching dev-route entries appended after.
const wildcardRoute = allRoutes.find(r => r.path === '**');
const nonWildcardRoutes = allRoutes.filter(r => r.path !== '**');
export const routes: Routes = environment.features.enableDevTools
  ? wildcardRoute
    ? [...nonWildcardRoutes, ...devRoutes, wildcardRoute]
    : [...nonWildcardRoutes, ...devRoutes]
  : allRoutes.filter(isProductionRoute);

/**
 * GAP-FE-001 · Derive the operator-facing route inventory directly from the
 * live route table so the menu/route drift detection can never go stale.
 *
 * Excludes:
 *   - empty path (root redirect to /dashboard)
 *   - `**` wildcard
 *   - `auth/**` (unauthenticated)
 *   - `tenant-selection`, `unauthorized` (no operator chrome)
 *   - dev-only routes (canActivate includes `devToolsGuard`)
 *   - pure redirect aliases (have `redirectTo` but no `loadChildren`/`loadComponent`)
 *
 * Everything that survives the filter is an operator-facing path. The menu
 * spec asserts each derived path is either in `buildDefaultMenu()` or in an
 * explicit `INTENTIONALLY_PALETTE_ONLY_ROUTES` allowlist. Adding a new feature
 * route to this table without wiring a menu entry will now fail the regression
 * test automatically — no hand-maintained list to forget.
 */
export function getOperatorFacingRoutePaths(): readonly string[] {
  const out: string[] = [];
  for (const r of allRoutes) {
    const path = r.path;
    if (path === undefined || path === '' || path === '**') continue;
    if (path === 'auth' || path.startsWith('auth/')) continue;
    if (path === 'tenant-selection' || path === 'unauthorized') continue;
    const guards = (r.canActivate ?? []) as readonly unknown[];
    if (guards.includes(devToolsGuard)) continue;
    // Pure redirect alias (no loaded surface)
    if (r.redirectTo !== undefined && r.loadChildren === undefined && r.loadComponent === undefined) {
      continue;
    }
    out.push(`/${path}`);
  }
  return out;
}
